import { Fragment, useEffect, useState } from "react";
import api from "@/lib/api";
import { Check, Minus, ChevronDown, ChevronRight, Loader2, ShieldCheck } from "lucide-react";

// System roles shown first, in privilege order. "disabled" is a sign-in state, not a capability set.
const ROLE_ORDER = ["super-admin", "admin", "tester", "read-only"];

/**
 * Read-only matrix of which role holds which permission. Sources the live RBAC
 * catalog (/admin/permissions) and role definitions (/admin/roles) — both
 * gated to admin/super-admin — so it always reflects reality. Super-admin is
 * always shown as fully capable (backend grants ALL_PERMISSIONS regardless of
 * the stored doc). Collapsed by default; fetches on first open.
 */
export default function RoleCapabilityMatrix() {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [perms, setPerms] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (!open || loaded) return;
    setLoading(true);
    Promise.all([api.get("/admin/permissions"), api.get("/admin/roles")])
      .then(([p, r]) => {
        setPerms(p.data);
        setRoles(r.data);
        setLoaded(true);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, loaded]);

  const cols = [
    ...ROLE_ORDER.filter((n) => roles.some((r) => r.name === n)),
    ...roles.map((r) => r.name).filter((n) => !ROLE_ORDER.includes(n) && n !== "disabled"),
  ];
  const roleByName = Object.fromEntries(roles.map((r) => [r.name, r]));
  const can = (roleName, key) =>
    roleName === "super-admin" || (roleByName[roleName]?.permissions || []).includes(key);

  const cats = [];
  perms.forEach((p) => {
    let c = cats.find((x) => x.name === p.category);
    if (!c) {
      c = { name: p.category, items: [] };
      cats.push(c);
    }
    c.items.push(p);
  });

  return (
    <div data-testid="role-capability-matrix" className="border border-[#33424f]/20 bg-white mb-6">
      <button
        data-testid="toggle-capability-matrix"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#f5f1ec] transition-colors"
      >
        <span className="flex items-center gap-2 text-xs subtitle uppercase tracking-[0.2em] text-[#33424f]">
          <ShieldCheck className="w-4 h-4 text-[#39678f]" /> Role capabilities
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-[#50493b]" /> : <ChevronRight className="w-4 h-4 text-[#50493b]" />}
      </button>

      {open && (
        <div className="border-t border-[#33424f]/15 p-4 overflow-x-auto">
          {loading && (
            <div className="text-sm text-[#50493b] font-body py-4 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && cols.length > 0 && (
            <>
              <p className="font-body text-xs text-[#50493b] mb-3 max-w-2xl">
                What each role can do in the admin console. Super-admin has every capability. Tester
                &amp; read-only carry no management permissions (their registry access is governed
                separately). Custom roles appear here automatically.
              </p>
              <table className="text-xs data-table min-w-[640px]">
                <thead>
                  <tr>
                    <th className="text-left px-3 py-2">Capability</th>
                    {cols.map((c) => (
                      <th key={c} className="px-3 py-2 text-center whitespace-nowrap capitalize">
                        {c.replace("-", " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cats.map((cat) => (
                    <Fragment key={cat.name}>
                      <tr>
                        <td
                          colSpan={cols.length + 1}
                          className="px-3 pt-3 pb-1 text-[10px] subtitle uppercase tracking-[0.2em] text-[#39678f]"
                        >
                          {cat.name}
                        </td>
                      </tr>
                      {cat.items.map((it) => (
                        <tr key={it.key} className="border-t border-[#33424f]/10">
                          <td className="px-3 py-1.5 font-body text-[#33424f]">{it.name}</td>
                          {cols.map((c) => (
                            <td
                              key={c}
                              data-testid={`cap-${c}-${it.key}`}
                              className="px-3 py-1.5 text-center"
                            >
                              {can(c, it.key) ? (
                                <Check className="w-4 h-4 text-[#2d5e1f] inline" />
                              ) : (
                                <Minus className="w-3.5 h-3.5 text-[#bdb4a3] inline" />
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
